// The typed errors this middleware throws once `fetch` has resolved with a
// Response: a non-2xx status, or an `X-Echo` that doesn't match the one the
// request sent. Errors from the fetch call itself (network failure, abort,
// malformed request) are left as whatever `window.fetch` naturally throws.

type ErrorResponseBody = {
  response: Response
  text?: string
  json?: unknown
}

export class FetchError extends Error {
  constructor(
    public url: string,
    public method: string,
    public status: number,
    public response: ErrorResponseBody
  ) {
    super(`${method} ${url} responded with ${status}`)
    this.name = 'FetchError'
  }
}

/**
 * Handles a successful `fetch` Response, throwing a `FetchError` when
 * !response.ok, with the body attached as `json` or `text` depending on
 * Content-Type.
 */
export async function throwForHttpError(request: Request, response: Response): Promise<Response> {
  if (response.ok) return response

  const {status, headers} = response
  const {method, url} = request

  const contentType = headers.get('content-type')
  const clonedResponse = response.clone()

  if (contentType !== null && contentType.includes('application/json')) {
    throw new FetchError(url, method, status, {
      response: clonedResponse,
      json: await clonedResponse.json()
    })
  }

  // API returned unexpected non-2xx response with non-JSON body.
  throw new FetchError(url, method, status, {
    response: clonedResponse,
    text: await clonedResponse.text()
  })
}

export class EchoMismatchError extends Error {
  constructor(
    public url: string,
    public method: string,
    public status: number,
    public expectedEcho: string,
    public receivedEcho: string
  ) {
    super(`${method} ${url} responded with X-Echo "${receivedEcho}", expected "${expectedEcho}"`)
    this.name = 'EchoMismatchError'
  }
}

/**
 * Throws an `EchoMismatchError` when a response carries an `X-Echo` other than
 * the echo this request sent, meaning the body belongs to some other request —
 * a caching proxy serving a stale response, or responses crossing between
 * users. Responses with no `X-Echo` at all never reached `respondToEcho` (an
 * error page from a proxy in front of the API), so there is nothing to compare.
 */
export function throwForEchoMismatch(
  request: Request,
  response: Response,
  expectedEcho: string
): void {
  const receivedEcho = response.headers.get('X-Echo')
  if (receivedEcho === null || receivedEcho === expectedEcho) return

  throw new EchoMismatchError(
    request.url,
    request.method,
    response.status,
    expectedEcho,
    receivedEcho
  )
}
